import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeRequestAuth } from '../../_config/api';
import { showErrorToast } from '../../utils/toastUtils';
import { FiArrowLeft, FiCalendar, FiDollarSign, FiBriefcase, FiUser, FiHash, FiFileText, FiClock } from 'react-icons/fi';
import { ToastContainer } from 'react-toastify';

export default function PayoutDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [payout, setPayout] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');

    const getFilteredDuties = () => {
        if (!payout?.duties) return [];
        let duties = [...payout.duties].sort((a, b) => new Date(b.serviceDate) - new Date(a.serviceDate));
        if (selectedDate) {
            duties = duties.filter(duty => new Date(duty.serviceDate).toISOString().split('T')[0] === selectedDate);
        }
        return duties;
    };

    const fetchPayoutDetails = async () => {
        try {
            setLoading(true);
            const res = await makeRequestAuth("post", "/admin/getPayoutDetails", { id });
            if (!res.error) {
                setPayout(res.data);
            } else {
                showErrorToast(res.message || "Failed to load details");
            }
        } catch (err) {
            showErrorToast("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPayoutDetails();
    }, [id]);

    if (loading) return <div className="p-20 text-center font-bold text-slate-400">Loading payout details...</div>;
    if (!payout) return <div className="p-20 text-center font-bold text-red-500">Payout record not found</div>;

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-2 md:py-4 md:px-6">
            <div className="max-w-7xl mx-auto">
                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-slate-400 hover:text-[#24ADD7] hover:shadow-lg transition-all border border-slate-100"
                        >
                            <FiArrowLeft size={20} />
                        </button>
                        <div>
                            <h2 className="text-3xl font-black text-slate-800 uppercase italic tracking-tight leading-none">Payout Details</h2>
                            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2 flex items-center gap-2">
                                <FiCalendar className="text-[#24ADD7]" />
                                {new Date(payout.month + "-01").toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                        <div className={`px-6 py-2 rounded-2xl text-xs font-black uppercase tracking-widest border-2 ${payout.status === 'paid' ? 'bg-green-50 border-green-200 text-green-600' : 'bg-orange-50 border-orange-200 text-orange-600'}`}>
                            {payout.status}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* LEFT COL: INFO CARDS */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* AGENT CARD */}
                        <div className="bg-white rounded-[40px] p-6 shadow-sm border border-slate-100 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#24ADD7]/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-[#24ADD7]/10 transition-colors"></div>
                            <div className="relative z-10">
                                <span className="text-[10px] font-black text-[#24ADD7] uppercase tracking-widest block mb-3">Service Professional</span>
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-[#24ADD7]">
                                        <FiUser size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-slate-800 leading-tight">{payout.agentId?.name}</h3>
                                        <p className="text-slate-400 text-[10px] font-bold">{payout.agentId?.phone}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FINANCIAL SUMMARY */}
                        <div className="bg-slate-900 rounded-[40px] p-6 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-[#24ADD7]/20 rounded-full blur-[80px] -mr-20 -mt-20"></div>
                            <div className="relative z-10 space-y-4">
                                <div>
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Total Payout</span>
                                    <h4 className="text-3xl font-black text-white italic tracking-tighter">₹{payout.agentShare}</h4>
                                </div>
                                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
                                    <div>
                                        <span className="text-[9px] font-black text-slate-500 uppercase block">Revenue</span>
                                        <p className="text-base font-bold text-slate-300 tracking-tight">₹{payout.totalRevenue}</p>
                                    </div>
                                    <div>
                                        <span className="text-[9px] font-black text-slate-500 uppercase block">Tasks</span>
                                        <p className="text-base font-bold text-slate-300 tracking-tight">{payout.totalTasks} Jobs</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SETTLEMENT DETAILS */}
                        <div className="bg-white rounded-[40px] p-6 shadow-sm border border-slate-100">
                            <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-4 border-b border-slate-50 pb-3">Payment Info</h4>
                            <div className="space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400"><FiHash size={18} /></div>
                                    <div>
                                        <span className="text-[9px] font-black text-slate-400 uppercase block">Transaction ID</span>
                                        <p className="text-xs font-bold text-slate-700 break-all">{payout.transactionId || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400"><FiClock size={18} /></div>
                                    <div>
                                        <span className="text-[9px] font-black text-slate-400 uppercase block">Settled On</span>
                                        <p className="text-xs font-bold text-slate-700">{payout.paymentDate ? new Date(payout.paymentDate).toLocaleString() : 'Pending'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COL: DUTIES LIST */}
                    {/* RIGHT COL: DUTIES LIST */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                            <div className="px-6 py-6 border-b border-slate-50 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-2 h-8 bg-[#24ADD7] rounded-full"></div>
                                    <h4 className="text-xl font-black text-slate-800 uppercase italic tracking-tight">Included Jobs</h4>
                                </div>
                                <div className="flex items-center gap-4">
                                    <input 
                                        type="date" 
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 outline-none focus:border-[#24ADD7] transition-all"
                                    />
                                    {selectedDate && (
                                        <button 
                                            onClick={() => setSelectedDate('')}
                                            className="text-[10px] font-bold text-red-400 hover:text-red-500 uppercase tracking-widest"
                                        >
                                            Clear
                                        </button>
                                    )}
                                    <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest">{payout.totalTasks} Total</span>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            {/* Yahan px-8 ko px-4 kar diya gaya hai taaki extra space bache */}
                                            <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date & Time</th>
                                            <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Service</th>
                                            <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Requirement</th>
                                            <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Admin Note</th>
                                            <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Amount</th>
                                            <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Commission</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {getFilteredDuties().length > 0 ? getFilteredDuties().map((duty, index) => (
                                            <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                                                {/* td ke andar bhi px-4 kar diya hai */}
                                                <td className="px-4 py-5">
                                                    <p className="text-sm font-bold text-slate-700">{new Date(duty.serviceDate).toLocaleDateString()}</p>
                                                    <div className="flex flex-col mt-1">
                                                        <p className="text-[10px] text-slate-500 font-medium">{duty.serviceTimeSlot}</p>

                                                        {/* Yahan par fix kiya gaya hai. Sirf time dikhayenge agar paidAt hai, taaki date repeat na ho */}
                                                        {duty.paidAt && (
                                                            <div className="inline-block mt-1">
                                                                <span className="bg-green-50 text-green-600 text-[9px] font-bold px-2 py-0.5 rounded-md">
                                                                    Paid at {new Date(duty.paidAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <span className="text-sm font-bold text-slate-600">{duty.serviceType?.name}</span>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <p className="text-xs font-medium text-slate-500 italic max-w-[120px] truncate" title={duty.message}>
                                                        {duty.message || "—"}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <p className="text-xs font-bold text-green-600 italic truncate max-w-[100px]" title={duty.adminComment}>
                                                        {duty.adminComment || "—"}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-5 text-sm font-bold text-slate-600">₹{duty.serviceFee}</td>
                                                <td className="px-4 py-5 text-sm font-black text-[#24ADD7]">₹{Math.round(duty.serviceFee * 0.10)}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="6" className="px-4 py-10 text-center font-bold text-slate-400">
                                                    No jobs found for this date.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </div>
    );
}
